import React from 'react';
import { Box, Button, IconButton, Stack, Typography, alpha, keyframes, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import type { TooltipRenderProps } from 'react-joyride';

// Entrance animation — fades + slides up + slight scale. Plays every time
// react-joyride mounts the tooltip (i.e. on every step). Curve uses a soft
// overshoot so the card lands with a tiny "settle" instead of a hard stop.
const tooltipEnter = keyframes`
  0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
  60%  { opacity: 1; transform: translateY(-1px) scale(1.005); }
  100% { opacity: 1; transform: translateY(0)    scale(1); }
`;

/**
 * Custom tooltip rendered by react-joyride v3 via the `tooltipComponent` prop.
 * Replaces the library's default chrome with a polished card that matches
 * the portal's grammar: Paper-bordered surface, generous spacing, MUI
 * Typography for hierarchy, a row of progress dots, and theme-tinted CTAs.
 *
 * Joyride hands us `tooltipProps` (which include refs + roles for a11y),
 * plus pre-wired button props for back / next / skip / close. We just style
 * the shell — behaviour stays inside joyride's state machine.
 */
const TourTooltip: React.FC<TooltipRenderProps> = ({
  step,
  index,
  size,
  isLastStep,
  backProps,
  primaryProps,
  skipProps,
  closeProps,
  tooltipProps,
  continuous,
}) => {
  const theme = useTheme();
  const stepNumber = index + 1;

  return (
    <Box
      {...tooltipProps}
      // React reconciles the custom tooltipComponent across steps; using the
      // step index as `key` forces a fresh mount per step so the entrance
      // animation re-runs every time.
      key={`tour-tooltip-${index}`}
      sx={{
        // Narrower default so the card fits comfortably in the app-sidebar
        // gutter when the tour spotlights big content panels.
        width: { xs: 280, sm: 340 },
        maxWidth: 'calc(100vw - 32px)',
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
        position: 'relative',
        animation: `${tooltipEnter} 280ms cubic-bezier(0.34, 1.12, 0.5, 1) both`,
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      {/* Accent bar — primary-toned hairline so the card has a touch of brand. */}
      <Box
        sx={{
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.5)})`,
        }}
      />

      {/* Close (X) — top right, subtle */}
      <IconButton
        {...closeProps}
        size="small"
        aria-label="Close tour"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'text.disabled',
          '&:hover': { color: 'text.secondary', bgcolor: alpha(theme.palette.text.primary, 0.04) },
        }}
      >
        <Close sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Body */}
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              fontSize: '0.68rem',
            }}
          >
            Step {stepNumber} of {size}
          </Typography>
        </Stack>

        {step.title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              lineHeight: 1.25,
              mb: 1,
              pr: 3, // breathing room next to the close button
            }}
          >
            {step.title}
          </Typography>
        )}

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.6,
          }}
        >
          {step.content}
        </Typography>

        {/* Progress dots */}
        <Stack direction="row" spacing={0.75} sx={{ mt: 2.5, mb: 0.5 }}>
          {Array.from({ length: size }).map((_, i) => {
            const active = i === index;
            const done = i < index;
            return (
              <Box
                key={i}
                sx={{
                  width: active ? 18 : 6,
                  height: 6,
                  borderRadius: 999,
                  bgcolor: active
                    ? theme.palette.primary.main
                    : done
                      ? alpha(theme.palette.primary.main, 0.45)
                      : alpha(theme.palette.text.primary, 0.12),
                  transition: 'width 200ms ease, background-color 200ms ease',
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {/* Footer — Skip on the left, Back + Next on the right */}
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Button
          {...skipProps}
          size="small"
          sx={{
            textTransform: 'none',
            color: 'text.disabled',
            fontWeight: 500,
            fontSize: '0.8125rem',
            '&:hover': { bgcolor: 'transparent', color: 'text.secondary' },
          }}
        >
          Skip tour
        </Button>

        <Stack direction="row" spacing={1}>
          {index > 0 && (
            <Button
              {...backProps}
              size="small"
              variant="outlined"
              color="inherit"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                borderColor: theme.palette.divider,
                color: 'text.primary',
                fontWeight: 500,
                px: 2,
                '&:hover': {
                  borderColor: theme.palette.text.disabled,
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                },
              }}
            >
              Back
            </Button>
          )}
          <Button
            {...primaryProps}
            size="small"
            variant="contained"
            disableElevation
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
              px: 2.5,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
            }}
          >
            {isLastStep ? 'Done' : continuous ? 'Next' : 'Got it'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default TourTooltip;
